import React, { FC, useState, useEffect } from "react";
import { Tabs, Input, Select, Modal, Cascader, Table, Typography, Button, Tooltip } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import {
  ExternalMatter,
  AccruedFee,
  MatterFilter,
  newDateRangeFilter,
  BaseAccruedFee,
  RelevantMatter,
} from "../../data-types";
import { RelevantMatterList } from "./relevant-matter-list";
import { HistoricComparison } from "../historic-info/historic-comparison";
import {
  FindRelevantMattersQuery,
  FindRelevantExternalMattersQuery,
  MatterStatus,
  RateReplacement,
} from "~/gql/graphql";
import styles from "./create-proposal.css";
import { RelevantMatterPage } from "./relevant-matter-page";
import { RelevantExternalMatterPage } from "./relevant-external-matter-page";
import { useQuery } from "@apollo/client";
import { GET_CURRENCIES } from "~/graphql/query";
import { TeamOutlined, DeleteOutlined } from "@ant-design/icons";
import { useAllFeeEarnerLevelsAndFeeEarnersQuery } from "~/hooks";
import { AllFeeEarnerLevelsAndFeeEarnersOption } from "~/hooks/use-all-fee-earner-levels-and-fee-earners-query";
import { VPDLabel } from "~/context/taxonomy-context";

const {
  ["search-input"]: searchInputClassName,
  ["tab-bar-extra-content"]: tabBarExtraContentClassName,
  ["currency-select-wrapper"]: currencySelectWrapperClassName,
  ["currency-select"]: currencySelectClassName,
  ["search-input-wrapper"]: searchInputWrapperClassName,
  ["icon-circle"]: iconCircleClassName,
} = styles;

export enum MatterTitleScreenTabKey {
  TemplateMatters = "1",
  HistoricalMatters = "2",
  HistoricalComparison = "3",
}

type RelevantMatterTabsProps = {
  createMode: boolean;
  activeTab: MatterTitleScreenTabKey;
  setActiveTab: (key: MatterTitleScreenTabKey) => void;
  selectedMatter: RelevantMatter | null;
  selectedAccruedFees: AccruedFee | null;
  handleSelectedMatterChange: (matter: RelevantMatter | null) => void;
  selectedExternalMatter: ExternalMatter | null;
  selectedExternalAccruedFees: BaseAccruedFee | null;
  handleSelectedExternalMatterChange: (matter: ExternalMatter | null) => void;
  templateMatters: FindRelevantMattersQuery | undefined;
  historicalMatters: FindRelevantMattersQuery | undefined;
  historicalComparisonLoading: boolean;
  externalMatters: FindRelevantExternalMattersQuery | undefined;
  additionalSearchText: string;
  setAdditionalSearchText: (text: string) => void;
  templateMattersLoading: boolean;
  historicalMattersLoading: boolean;
  maxHeight: string;
  onCopyScope?: () => void;
  onCopyResourcing?: (rateReplacements: RateReplacement[]) => void;
  onCopyPricing?: (rateReplacements: RateReplacement[]) => void;
  onCopyAll?: (rateReplacements: RateReplacement[]) => void;
  matterFilter: MatterFilter;
  handleFilterChange: (value: MatterFilter) => void;
  allowChangeCurrency: boolean;
  originalMatterId?: number;
  onPinToMatter?: (matterId: number) => void;
  pinnedMatterIds?: number[];
  onFavouriteMatter?: (matterId: number) => void;
};

const mapFeeEarnerIdsToInfo = (
  feeEarnerIds: number[] | undefined,
  feeEarnerLevelsAndFeeEarners: AllFeeEarnerLevelsAndFeeEarnersOption<boolean>[] | undefined
) => {
  return (feeEarnerIds ?? [])
    .map((id) => {
      const level = feeEarnerLevelsAndFeeEarners?.find((level) =>
        level.children?.some((earner) => earner.value === id)
      );
      const earner = level?.children?.find((e) => e.value === id);
      return earner && level
        ? {
            feeEarnerId: id,
            feeEarner: earner.label as string,
            feeEarnerLevel: level.label,
          }
        : null;
    })
    .filter((f): f is NonNullable<typeof f> => f !== null);
};

export const RelevantMatterTabs: FC<RelevantMatterTabsProps> = ({
  createMode,
  activeTab,
  setActiveTab,
  selectedMatter,
  selectedAccruedFees,
  handleSelectedMatterChange,
  selectedExternalMatter,
  selectedExternalAccruedFees,
  handleSelectedExternalMatterChange,
  templateMatters,
  historicalMatters,
  historicalComparisonLoading,
  externalMatters,
  additionalSearchText,
  setAdditionalSearchText,
  templateMattersLoading,
  historicalMattersLoading,
  maxHeight,
  onCopyScope,
  onCopyResourcing,
  onCopyPricing,
  onCopyAll,
  matterFilter,
  handleFilterChange,
  allowChangeCurrency,
  onPinToMatter,
  pinnedMatterIds,
  onFavouriteMatter,
}) => {
  const FE_LABEL = VPDLabel("FeeEarner");
  const TEMPLATE_LABEL = VPDLabel("Template");

  const [feeEarnerFilterModalOpen, setFeeEarnerFilterModalOpen] = useState(false);
  const [stagedFeeEarnerFilter, setStagedFeeEarnerFilter] = useState<
    {
      feeEarnerId: number;
      feeEarner: string;
      feeEarnerLevel: string;
    }[]
  >([]);
  const [cascaderValue, setCascaderValue] = useState<any[]>([]);
  const { data: currencies } = useQuery(GET_CURRENCIES);
  const isMultiCurrencyInstance = (currencies?.currencies?.length ?? 0) > 1;
  const currencySymbol =
    currencies?.currencies.find((c) => c.id === matterFilter.currencyId?.[0])?.symbol ?? "";

  const { feeEarnerLevelsAndFeeEarners } = useAllFeeEarnerLevelsAndFeeEarnersQuery(false, true);
  const feeEarnerOptions = feeEarnerLevelsAndFeeEarners?.map((level) => {
    return {
      label: level.label,
      value: level.value,
      children: level.children
        ?.filter((earner) => !earner.isFeeEarnerLevelRate)
        .map((earner) => ({
          label: earner.label,
          value: earner.value,
        })),
    };
  });

  useEffect(() => {
    if (matterFilter.feeEarner) {
      setStagedFeeEarnerFilter(
        mapFeeEarnerIdsToInfo(matterFilter.feeEarner, feeEarnerLevelsAndFeeEarners)
      );
    }
  }, [matterFilter.feeEarner]);

  const onFilterChange = (filters: Record<string, FilterValue | null>) => {
    handleFilterChange({
      ...matterFilter,
      client: filters.client as number[],
      orgUnit: Array.isArray(filters.orgUnit) ? (filters.orgUnit[0] as number) : null,
      updatedAt: newDateRangeFilter(filters.updatedAt as number[]),
      status: filters.status as MatterStatus[],
      title: filters.title ? (filters.title[0] as string) : null,
      someTimeEntries: filters.actualGpm?.includes("hasTimeEntries") || null,
    });
  };

  const renderRelevantMatterPage = () => {
    if (selectedMatter) {
      return (
        <RelevantMatterPage
          matter={selectedMatter}
          accruedFees={selectedAccruedFees ?? undefined}
          onDeselectMatter={() => handleSelectedMatterChange(null)}
          maxHeight={maxHeight}
          onCopyScope={onCopyScope}
          onCopyResourcing={onCopyResourcing}
          onCopyPricing={onCopyPricing}
          onCopyAll={onCopyAll}
          onPinToMatter={onPinToMatter}
          pinned={pinnedMatterIds?.includes(selectedMatter.id)}
        />
      );
    } else if (selectedExternalMatter) {
      return (
        <RelevantExternalMatterPage
          externalMatter={selectedExternalMatter}
          accruedFees={selectedExternalAccruedFees ?? undefined}
          onDeselectMatter={() => handleSelectedExternalMatterChange(null)}
          maxHeight={maxHeight}
        />
      );
    }
    return null;
  };

  return (
    <>
      <Tabs
        defaultActiveKey={activeTab}
        onChange={(key) => {
          setActiveTab(key as MatterTitleScreenTabKey);
          handleSelectedMatterChange(null);
        }}
        tabBarExtraContent={
          !selectedMatter && !selectedExternalMatter ? (
            <div className={tabBarExtraContentClassName}>
              {activeTab !== MatterTitleScreenTabKey.TemplateMatters && (
                <>
                  Filter by {FE_LABEL}:
                  <div
                    className={iconCircleClassName}
                    onClick={() => setFeeEarnerFilterModalOpen(true)}
                  >
                    <TeamOutlined />
                    {stagedFeeEarnerFilter.length > 0 && (
                      <span className="ant-badge-count">{stagedFeeEarnerFilter.length}</span>
                    )}
                  </div>
                </>
              )}

              {isMultiCurrencyInstance &&
                allowChangeCurrency &&
                activeTab !== MatterTitleScreenTabKey.TemplateMatters && (
                  <>
                    Filter by currency:
                    <div className={currencySelectWrapperClassName}>
                      <Select
                        options={currencies?.currencies?.map((currency) => ({
                          label: currency.code,
                          value: currency.id,
                        }))}
                        placeholder="Select Currency"
                        defaultValue={matterFilter.currencyId?.[0]}
                        className={currencySelectClassName}
                        onChange={(value: number) => {
                          handleFilterChange({
                            ...matterFilter,
                            currencyId: [value],
                          });
                        }}
                      />
                    </div>
                  </>
                )}
              {setAdditionalSearchText && (
                <div className={searchInputWrapperClassName}>
                  <Tooltip
                    placement="bottom"
                    title="Search across matter titles, client names, and scope. You can enter multiple search terms separated by commas (e.g. 'contract, legal, agreement')"
                  >
                    <Input
                      placeholder="Enter additional search terms here"
                      value={additionalSearchText}
                      onChange={(e) => setAdditionalSearchText(e.target.value)}
                      className={searchInputClassName}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          ) : selectedExternalMatter && createMode ? (
            <div className={tabBarExtraContentClassName}>
              <Typography.Paragraph style={{ color: "var(--error-color)" }}>
                *This matter was not created in VPD and cannot be used to create a new proposal.
              </Typography.Paragraph>
            </div>
          ) : null
        }
      >
        {createMode && (
          <Tabs.TabPane tab={`${TEMPLATE_LABEL}`} key={MatterTitleScreenTabKey.TemplateMatters}>
            <RelevantMatterList
              matters={templateMatters?.findRelevantMatters?.matters || []}
              loading={templateMattersLoading}
              selectedMatter={selectedMatter}
              onMatterSelect={handleSelectedMatterChange}
              renderRelevantMatterPage={renderRelevantMatterPage}
              matterFilter={matterFilter}
              handleFilterChange={onFilterChange}
              additionalSearchText={additionalSearchText}
              onPinToMatter={onPinToMatter}
              pinnedMatterIds={pinnedMatterIds}
            />
          </Tabs.TabPane>
        )}
        <Tabs.TabPane tab="Historical Matters" key={MatterTitleScreenTabKey.HistoricalMatters}>
          <RelevantMatterList
            matters={historicalMatters?.findRelevantMatters?.matters || []}
            accruedFees={historicalMatters?.findRelevantMatters?.accruedFees || []}
            loading={historicalMattersLoading}
            selectedMatter={selectedMatter}
            onMatterSelect={handleSelectedMatterChange}
            renderRelevantMatterPage={renderRelevantMatterPage}
            matterFilter={matterFilter}
            handleFilterChange={onFilterChange}
            additionalSearchText={additionalSearchText}
            onPinToMatter={onPinToMatter}
            pinnedMatterIds={pinnedMatterIds}
            onFavouriteMatter={onFavouriteMatter}
          />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab="Historical Comparison"
          key={MatterTitleScreenTabKey.HistoricalComparison}
        >
          <HistoricComparison
            historicalMatters={historicalMatters?.findRelevantMatters?.matters || []}
            externalMatters={
              (externalMatters?.findRelevantExternalMatters
                ?.externalMatters as unknown as ExternalMatter[]) || []
            }
            accruedFees={historicalMatters?.findRelevantMatters?.accruedFees || []}
            externalAccruedFees={externalMatters?.findRelevantExternalMatters?.accruedFees || []}
            loading={historicalComparisonLoading}
            selectedMatter={selectedMatter}
            setSelectedMatter={handleSelectedMatterChange}
            selectedExternalMatter={selectedExternalMatter}
            setSelectedExternalMatter={handleSelectedExternalMatterChange}
            renderRelevantMatterPage={renderRelevantMatterPage}
            matterFilter={matterFilter}
            handleFilterChange={onFilterChange}
            activeTab={activeTab}
            currencySymbol={currencySymbol}
          />
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title={`${FE_LABEL} Filter`}
        open={feeEarnerFilterModalOpen}
        onCancel={() => {
          setStagedFeeEarnerFilter(
            mapFeeEarnerIdsToInfo(matterFilter.feeEarner ?? [], feeEarnerLevelsAndFeeEarners)
          );
          setFeeEarnerFilterModalOpen(false);
        }}
        footer={[
          <Button
            key="reset"
            onClick={() => {
              setStagedFeeEarnerFilter([]);
              setFeeEarnerFilterModalOpen(false);
              handleFilterChange({
                ...matterFilter,
                feeEarner: [],
              });
            }}
          >
            Reset
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={() => {
              handleFilterChange({
                ...matterFilter,
                feeEarner: stagedFeeEarnerFilter.map((f) => f.feeEarnerId),
              });
              setFeeEarnerFilterModalOpen(false);
            }}
          >
            Apply
          </Button>,
        ]}
      >
        <Typography.Paragraph>
          Select {FE_LABEL}s to filter by. The results will include matters where any of the
          selected {FE_LABEL}s have worked on the matter.
        </Typography.Paragraph>
        <Cascader
          options={feeEarnerOptions}
          placeholder={`Select ${FE_LABEL}`}
          showSearch={true}
          style={{ width: "100%", marginBottom: "10px" }}
          maxTagCount="responsive"
          value={cascaderValue}
          onChange={(value: any, selectedOptions: any) => {
            if (
              selectedOptions?.[1] &&
              !stagedFeeEarnerFilter.some((f) => f.feeEarnerId === selectedOptions[1].value)
            ) {
              setStagedFeeEarnerFilter([
                ...stagedFeeEarnerFilter,
                {
                  feeEarnerId: selectedOptions[1].value,
                  feeEarner: selectedOptions[1].label,
                  feeEarnerLevel: selectedOptions[0].label,
                },
              ]);
              setCascaderValue([]);
            }
          }}
        />
        <Table
          columns={[
            {
              title: `${FE_LABEL}`,
              dataIndex: "feeEarner",
            },
            {
              title: `${VPDLabel("FeeEarnerLevel")}`,
              dataIndex: "feeEarnerLevel",
            },
            {
              title: "Remove",
              key: "remove",
              align: "center",
              render: (_, record) => (
                <DeleteOutlined
                  style={{ color: "var(--error-color)" }}
                  onClick={() =>
                    setStagedFeeEarnerFilter((prev) =>
                      prev.filter((item) => item.feeEarnerId !== record.feeEarnerId)
                    )
                  }
                />
              ),
            },
          ]}
          dataSource={stagedFeeEarnerFilter}
          pagination={false}
        />
      </Modal>
    </>
  );
};
